import { useCallback, useEffect, useState } from 'react';
import { Check, Globe, Loader, Plus, RefreshCw, Sparkles, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import MemoryPanel from '../components/MemoryPanel';
import { PageLoader } from '../components/PageLoader';
import type { MarketPage as Page, MarketView } from '../lib/types';

const SECTION_LABELS: Record<string, string> = {
    market: 'Market', competitors: 'Competitors', technologies: 'Technologies',
    regulation: 'Regulation', news: 'News', events: 'Events',
};
const BUTTON = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg disabled:opacity-40 cursor-pointer';

// The market: Navaris's view of the world the company works in, written from the outside
// pages it follows, and those pages, which Navaris proposes and a person chooses.
export default function MarketPage() {
    const { authFetch } = useAuth();
    const { showToast } = useToast();
    const [view, setView] = useState<MarketView | null>(null);
    const [url, setUrl] = useState('');
    const [section, setSection] = useState('news');
    const [busy, setBusy] = useState(false);

    const reload = useCallback(() => {
        authFetch('/market')
            .then(r => r.ok ? r.json() : null)
            .then(setView)
            .catch(() => setView(null));
    }, [authFetch]);
    useEffect(reload, [reload]);

    const post = async (path: string, body?: unknown) => {
        setBusy(true);
        try {
            const res = await authFetch(path, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: body === undefined ? undefined : JSON.stringify(body),
            });
            if (!res.ok) {
                const detail = (await res.json().catch(() => ({}))).detail;
                throw new Error(detail || 'That did not work');
            }
            const data = await res.json().catch(() => ({}));
            reload();
            return data;
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'That did not work', 'error');
            return null;
        } finally {
            setBusy(false);
        }
    };
    const decide = (pages: Page[], status: 'active' | 'dismissed') =>
        post('/market/pages/decide', { urls: pages.map(p => p.url), status });
    const add = async (e: React.FormEvent) => {
        e.preventDefault();
        if (url.trim() && await post('/market/pages', { url: url.trim(), section })) setUrl('');
    };
    const refresh = async () => {
        const reading = (view?.watchlist ?? []).some(p => p.status === 'active');
        const waiting = (view?.watchlist ?? []).some(p => p.status === 'proposed');
        const result = await post('/market/refresh');
        if (!result) return;
        const suggesting = !!result.proposal_job_id;
        if (reading && suggesting) showToast('Navaris is reading the pages you follow and looking for more to suggest', 'success');
        else if (reading) showToast('Navaris is reading the pages you follow now', 'success');
        else if (suggesting) showToast('Navaris is looking for pages to suggest. They will appear here shortly.', 'success');
        else if (waiting) showToast('Follow or dismiss the suggested pages first. Navaris suggests more once they are answered or the record changes.', 'info');
        else showToast('Navaris suggests pages once the record says what the company does. Connect its website or file a document that describes it.', 'info');
    };

    if (view === null) return <PageLoader label="Loading the market..." />;
    const proposed = view.watchlist.filter(p => p.status === 'proposed');
    const active = view.watchlist.filter(p => p.status === 'active');

    const row = (p: Page, actions: React.ReactNode) => (
        <div key={p.url} className="flex items-start gap-3 px-3 py-3">
            <Globe className="w-4 h-4 mt-0.5 shrink-0 text-coolslate" aria-hidden />
            <div className="min-w-0 flex-1">
                <a href={p.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-graphite break-all hover:text-brand-dark">
                    {p.url}
                </a>
                <p className="text-xs text-coolslate mt-0.5">
                    {SECTION_LABELS[p.section] || p.section}{p.why ? ` · ${p.why}` : ''}
                </p>
            </div>
            <div className="flex gap-1 shrink-0">{actions}</div>
        </div>
    );

    return (
        <div data-agent-view className="space-y-8">
            <div className="pb-6 border-b border-softgrey flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold">Market</h3>
                    <p className="text-sm text-coolslate mt-1">
                        Know what is moving around the company: its market, competitors, technologies,
                        regulation, news and events, from the outside pages Navaris follows. It sits
                        apart from what the company says of itself, and every point names its source.
                    </p>
                </div>
                <button onClick={refresh} disabled={busy} className={`${BUTTON} border border-softgrey text-graphite hover:border-brand/40`}>
                    <RefreshCw className="w-3.5 h-3.5" />Check now
                </button>
            </div>

            <MemoryPanel scope="market" />

            <section className="space-y-3">
                <h4 className="text-sm font-semibold text-graphite">Pages Navaris follows</h4>
                {!view.ready && view.watchlist.length === 0 && (
                    <p className="text-sm text-coolslate">
                        Navaris suggests pages once the company's record says what it does: its own account
                        of itself, its competitors, markets or products. Add a page yourself meanwhile.
                    </p>
                )}

                {proposed.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <p className="text-[13px] text-graphite inline-flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-brand-dark" aria-hidden />
                                Navaris suggests {proposed.length} page{proposed.length !== 1 ? 's' : ''}
                            </p>
                            <button onClick={() => decide(proposed, 'active')} disabled={busy}
                                className={`${BUTTON} bg-brand text-white hover:bg-brand-dark`}>
                                <Check className="w-3.5 h-3.5" />Follow all
                            </button>
                        </div>
                        <div className="border border-softgrey rounded-lg bg-white divide-y divide-softgrey">
                            {proposed.map(p => row(p, <>
                                <button onClick={() => decide([p], 'active')} disabled={busy} title="Follow"
                                    aria-label={`Follow ${p.url}`} className="p-1 text-brand-dark hover:text-graphite cursor-pointer">
                                    <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => decide([p], 'dismissed')} disabled={busy} title="Dismiss"
                                    aria-label={`Dismiss ${p.url}`} className="p-1 text-coolslate hover:text-graphite cursor-pointer">
                                    <X className="w-4 h-4" />
                                </button>
                            </>))}
                        </div>
                    </div>
                )}

                {active.length > 0 && (
                    <div className="border border-softgrey rounded-lg bg-white divide-y divide-softgrey">
                        {active.map(p => row(p,
                            <button onClick={() => decide([p], 'dismissed')} disabled={busy} title="Stop following"
                                aria-label={`Stop following ${p.url}`} className="p-1 text-coolslate hover:text-graphite cursor-pointer">
                                <X className="w-4 h-4" />
                            </button>,
                        ))}
                    </div>
                )}

                <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
                    <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/news"
                        className="flex-1 text-sm px-3 py-2 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand placeholder:text-coolslate" />
                    <select value={section} onChange={e => setSection(e.target.value)} aria-label="Section"
                        className="text-sm px-3 py-2 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand">
                        {view.sections.map(s => <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>)}
                    </select>
                    <button type="submit" disabled={busy || !url.trim()} className={`${BUTTON} justify-center bg-brand text-white hover:bg-brand-dark`}>
                        {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}Follow a page
                    </button>
                </form>
            </section>
        </div>
    );
}
